import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { ProgressBarModule } from 'primeng/progressbar';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { IftaLabelModule } from 'primeng/iftalabel';
import { PageFormSectionComponent } from 'src/app/shared/page-layout/page-form-section.component';
import { PageHeroComponent } from 'src/app/shared/page-layout/page-hero.component';
import { PageMetricStripComponent } from 'src/app/shared/page-layout/page-metric-strip.component';
import { PageMetric } from 'src/app/shared/page-layout/page-layout.models';
import { PageDataPanelComponent } from 'src/app/shared/page-layout/page-data-panel.component';
import { PageShellComponent } from 'src/app/shared/page-layout/page-shell.component';
import { Bank } from '../../bank-account/bank.model';
import { BankAccount } from '../../bank-account/bank-account.model';
import { BankAccountService } from '../../bank-account/bank-account.service';
import { ChartOfAccounts } from 'src/app/chart-of-accounts/chart-of-accounts.model';
import { ChartOfAccountsService } from 'src/app/chart-of-accounts/chart-of-accounts.service';
import { Party } from 'src/app/party/party.model';
import { PartyService } from 'src/app/party/party.service';
import { PartyType } from 'src/app/party/party-type.enum';
import { SessionStorageService } from 'src/app/services/session-storage.service';
import { CompanyService } from 'src/app/company/company.service';
import { TenantScopeService } from 'src/app/company/tenant-scope.service';
import { Company } from 'src/app/company/company.model';
import { Country } from 'src/app/models/common/country.model';

@Component({
    selector: 'app-banking-bank-account-form',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        RouterModule,
        ButtonModule,
        CheckboxModule,
        InputNumberModule,
        InputTextModule,
        SelectModule,
        ProgressBarModule,
        InputGroupModule,
        InputGroupAddonModule,
        IftaLabelModule,
        PageShellComponent,
        PageHeroComponent,
        PageMetricStripComponent,
        PageFormSectionComponent,
        PageDataPanelComponent
    ],
    template: `
        <div class="bank-account-page">
            <app-page-shell>
                <p-progressBar mode="indeterminate" *ngIf="loading || saving"></p-progressBar>

                <app-page-hero eyebrow="Banking setup" [title]="isEdit ? 'Edit Bank Account' : 'New Bank Account'"
                    [subtitle]="isEdit ? (account.accountName || 'Update bank account setup.') : 'Create a bank account and reconciliation setup profile.'">
                    <div actions>
                        <p-button type="button" label="Cancel" icon="pi pi-times" severity="secondary" [outlined]="true" (onClick)="cancel()"></p-button>
                        <p-button type="button" label="Save Account" icon="pi pi-check" [loading]="saving" [disabled]="loading || saving || !canSave" (onClick)="save()"></p-button>
                    </div>
                </app-page-hero>

                <app-page-metric-strip [metrics]="metrics"></app-page-metric-strip>

                @if (validationMessages.length) {
                    <section class="message-panel danger">
                        <strong>Resolve these fields before saving</strong>
                        @for (message of validationMessages; track message) {
                            <span>{{ message }}</span>
                        }
                    </section>
                }

                <div class="bank-account-workspace">
                    <div class="bank-account-main-column">
                        <app-page-form-section icon="pi pi-users" title="Owner" subtitle="Choose the party that owns this bank account.">
                            <div class="bank-form-grid two">
                                <div class="field">
                                    <p-inputGroup>
                                        <p-inputGroupAddon><i class="pi pi-sitemap"></i></p-inputGroupAddon>
                                        <p-iftalabel>
                                            <p-select inputId="ownerType" [options]="ownerTypes" [(ngModel)]="ownerType"
                                                optionLabel="label" optionValue="value" (onChange)="onOwnerTypeChange()"
                                                appendTo="body" styleClass="w-full"></p-select>
                                            <label for="ownerType">Owner type</label>
                                        </p-iftalabel>
                                    </p-inputGroup>
                                </div>
                                <div class="field">
                                    <p-inputGroup>
                                        <p-inputGroupAddon><i class="pi pi-user"></i></p-inputGroupAddon>
                                        <p-iftalabel>
                                            <p-select inputId="owner" [options]="partyOptions" [(ngModel)]="selectedOwner"
                                                optionLabel="entityName" [filter]="true" (onFilter)="searchParties($event.filter)"
                                                appendTo="body" styleClass="w-full"></p-select>
                                            <label for="owner">Owner</label>
                                        </p-iftalabel>
                                    </p-inputGroup>
                                </div>
                            </div>
                        </app-page-form-section>

                        <app-page-form-section icon="pi pi-building-columns" title="Bank Institution" subtitle="Select an existing bank master record.">
                            <div class="bank-form-grid two">
                                <div class="field">
                                    <p-inputGroup>
                                        <p-inputGroupAddon><i class="pi pi-building-columns"></i></p-inputGroupAddon>
                                        <p-iftalabel>
                                            <p-select inputId="bank" [options]="banks" [(ngModel)]="selectedBank"
                                                optionLabel="name" [filter]="true" appendTo="body" styleClass="w-full"
                                                [placeholder]="banks.length ? 'Select bank' : 'No banks loaded for this country'">
                                                <ng-template pTemplate="item" let-bank>
                                                    <div class="bank-option">
                                                        @if (bank.logoUrl) {
                                                            <img [src]="bank.logoUrl" alt="">
                                                        }
                                                        <div>
                                                            <strong>{{ bank.name }}</strong>
                                                            <small>{{ bank.swiftCode || bank.legalName || bank.countryName || 'No SWIFT' }}</small>
                                                        </div>
                                                    </div>
                                                </ng-template>
                                                <ng-template pTemplate="empty">
                                                    <div class="select-empty">No bank masters found for {{ companyCountryLabel }}.</div>
                                                </ng-template>
                                            </p-select>
                                            <label for="bank">Bank</label>
                                        </p-iftalabel>
                                    </p-inputGroup>
                                </div>
                                <div class="field">
                                    <p-inputGroup>
                                        <p-inputGroupAddon><i class="pi pi-map-marker"></i></p-inputGroupAddon>
                                        <p-iftalabel>
                                            <input id="country" pInputText [ngModel]="companyCountryLabel" disabled>
                                            <label for="country">Company country</label>
                                        </p-iftalabel>
                                    </p-inputGroup>
                                </div>
                            </div>
                        </app-page-form-section>

                        <app-page-form-section icon="pi pi-id-card" title="Account Details" subtitle="Capture identity, currency, account number, and IBAN.">
                            <div class="bank-form-grid four">
                                <div class="field">
                                    <p-inputGroup>
                                        <p-inputGroupAddon><i class="pi pi-wallet"></i></p-inputGroupAddon>
                                        <p-iftalabel>
                                            <input id="accountName" pInputText [(ngModel)]="account.accountName" placeholder="Main operating account">
                                            <label for="accountName">Account name</label>
                                        </p-iftalabel>
                                    </p-inputGroup>
                                </div>
                                <div class="field">
                                    <p-inputGroup>
                                        <p-inputGroupAddon><i class="pi pi-money-bill"></i></p-inputGroupAddon>
                                        <p-iftalabel>
                                            <p-select inputId="currency" [options]="currencies" [(ngModel)]="account.currencyCode"
                                                optionLabel="label" optionValue="value" appendTo="body" styleClass="w-full"></p-select>
                                            <label for="currency">Currency</label>
                                        </p-iftalabel>
                                    </p-inputGroup>
                                </div>
                                <div class="field">
                                    <p-inputGroup>
                                        <p-inputGroupAddon><i class="pi pi-hashtag"></i></p-inputGroupAddon>
                                        <p-iftalabel>
                                            <input id="accountNumber" pInputText [(ngModel)]="account.accountNumber" placeholder="Bank account number">
                                            <label for="accountNumber">Account number</label>
                                        </p-iftalabel>
                                    </p-inputGroup>
                                </div>
                                <div class="field">
                                    <p-inputGroup>
                                        <p-inputGroupAddon><i class="pi pi-credit-card"></i></p-inputGroupAddon>
                                        <p-iftalabel>
                                            <input id="iban" pInputText [(ngModel)]="account.iban" placeholder="SA...">
                                            <label for="iban">IBAN</label>
                                        </p-iftalabel>
                                    </p-inputGroup>
                                </div>
                            </div>
                        </app-page-form-section>

                        <app-page-form-section icon="pi pi-book" title="Accounting Linkage" subtitle="Map the bank account to the GL bank ledger account.">
                            <div class="bank-form-grid one">
                                <div class="field">
                                    <p-inputGroup>
                                        <p-inputGroupAddon><i class="pi pi-book"></i></p-inputGroupAddon>
                                        <p-iftalabel>
                                            <p-select inputId="glAccount" [options]="chartAccounts" [(ngModel)]="selectedGlAccount"
                                                optionLabel="accountName" [filter]="true" appendTo="body" styleClass="w-full">
                                                <ng-template pTemplate="item" let-glAccount>
                                                    <div class="cell-stack">
                                                        <strong>{{ glAccount.accountName }}</strong>
                                                        <small>{{ glAccount.accountCode }}</small>
                                                    </div>
                                                </ng-template>
                                            </p-select>
                                            <label for="glAccount">GL bank account</label>
                                        </p-iftalabel>
                                    </p-inputGroup>
                                </div>
                            </div>
                        </app-page-form-section>
                    </div>

                    <aside class="bank-account-side-column">
                        <app-page-data-panel title="Setup Summary" [resultText]="banks.length + ' banks'">
                            <div class="summary-grid">
                                <div>
                                    <span>Company</span>
                                    <strong>{{ activeCompany?.name || 'Current company' }}</strong>
                                </div>
                                <div>
                                    <span>Country</span>
                                    <strong>{{ companyCountryLabel }}</strong>
                                </div>
                                <div>
                                    <span>Bank master records</span>
                                    <strong [class.warn]="!banks.length">{{ banks.length }}</strong>
                                </div>
                                <div>
                                    <span>Reconciliation</span>
                                    <strong>{{ account.reconciliationEnabled ? 'Enabled' : 'Disabled' }}</strong>
                                </div>
                            </div>
                        </app-page-data-panel>

                        @if (warningMessages.length) {
                            <section class="message-panel warn">
                                <strong>Setup warnings</strong>
                                @for (message of warningMessages; track message) {
                                    <span>{{ message }}</span>
                                }
                            </section>
                        }
                    </aside>

                    <app-page-form-section icon="pi pi-sync" title="Reconciliation Settings" subtitle="Configure matching, tolerance, close behavior, and adjustment defaults.">
                        <div class="checkbox-row">
                            <p-checkbox [(ngModel)]="account.reconciliationEnabled" [binary]="true" inputId="reconciliationEnabled"></p-checkbox>
                            <label for="reconciliationEnabled">Enable bank reconciliation for this bank account</label>
                        </div>

                        <div class="bank-form-grid three">
                            <div class="field">
                                <p-inputGroup>
                                    <p-inputGroupAddon><i class="pi pi-percentage"></i></p-inputGroupAddon>
                                    <p-iftalabel>
                                        <p-inputNumber inputId="varianceTolerance" [(ngModel)]="account.reconciliationVarianceTolerance"
                                            mode="decimal" [minFractionDigits]="2" [maxFractionDigits]="4" [min]="0"></p-inputNumber>
                                        <label for="varianceTolerance">Variance tolerance</label>
                                    </p-iftalabel>
                                </p-inputGroup>
                            </div>
                            <div class="field span-2">
                                <p-inputGroup>
                                    <p-inputGroupAddon><i class="pi pi-calendar"></i></p-inputGroupAddon>
                                    <p-iftalabel>
                                        <input id="lastReconciledDate" pInputText [ngModel]="account.lastReconciledDate || 'Managed by close/reopen workflow'" disabled>
                                        <label for="lastReconciledDate">Last reconciled date</label>
                                    </p-iftalabel>
                                </p-inputGroup>
                            </div>
                            <div class="field">
                                <p-inputGroup>
                                    <p-inputGroupAddon><i class="pi pi-inbox"></i></p-inputGroupAddon>
                                    <p-iftalabel>
                                        <p-select inputId="defaultSuspenseAccount" [options]="chartAccounts" [(ngModel)]="account.defaultSuspenseAccountId"
                                            optionLabel="accountName" optionValue="accountId" [filter]="true" [showClear]="true"
                                            appendTo="body" styleClass="w-full"></p-select>
                                        <label for="defaultSuspenseAccount">Default suspense account</label>
                                    </p-iftalabel>
                                </p-inputGroup>
                            </div>
                            <div class="field">
                                <p-inputGroup>
                                    <p-inputGroupAddon><i class="pi pi-minus-circle"></i></p-inputGroupAddon>
                                    <p-iftalabel>
                                        <p-select inputId="bankFeesAccount" [options]="chartAccounts" [(ngModel)]="account.bankFeesAccountId"
                                            optionLabel="accountName" optionValue="accountId" [filter]="true" [showClear]="true"
                                            appendTo="body" styleClass="w-full"></p-select>
                                        <label for="bankFeesAccount">Bank fees account</label>
                                    </p-iftalabel>
                                </p-inputGroup>
                            </div>
                            <div class="field">
                                <p-inputGroup>
                                    <p-inputGroupAddon><i class="pi pi-arrow-down-left"></i></p-inputGroupAddon>
                                    <p-iftalabel>
                                        <p-select inputId="interestIncomeAccount" [options]="chartAccounts" [(ngModel)]="account.interestIncomeAccountId"
                                            optionLabel="accountName" optionValue="accountId" [filter]="true" [showClear]="true"
                                            appendTo="body" styleClass="w-full"></p-select>
                                        <label for="interestIncomeAccount">Interest income account</label>
                                    </p-iftalabel>
                                </p-inputGroup>
                            </div>
                            <div class="field">
                                <p-inputGroup>
                                    <p-inputGroupAddon><i class="pi pi-arrow-up-right"></i></p-inputGroupAddon>
                                    <p-iftalabel>
                                        <p-select inputId="interestExpenseAccount" [options]="chartAccounts" [(ngModel)]="account.interestExpenseAccountId"
                                            optionLabel="accountName" optionValue="accountId" [filter]="true" [showClear]="true"
                                            appendTo="body" styleClass="w-full"></p-select>
                                        <label for="interestExpenseAccount">Interest expense account</label>
                                    </p-iftalabel>
                                </p-inputGroup>
                            </div>
                        </div>
                    </app-page-form-section>
                </div>
            </app-page-shell>
        </div>
    `,
    styles: [`
        :host {
            display: block;
        }

        .bank-account-page {
            min-width: 0;
            padding: 0.75rem;
        }

        .bank-account-workspace {
            display: grid;
            grid-template-columns: minmax(0, 1fr) minmax(280px, 0.34fr);
            gap: 1rem;
            min-width: 0;
        }

        .bank-account-main-column,
        .bank-account-side-column {
            display: flex;
            flex-direction: column;
            gap: 1rem;
            min-width: 0;
        }

        .bank-account-workspace > app-page-form-section {
            grid-column: 1 / -1;
        }

        .bank-form-grid {
            display: grid;
            gap: 1rem;
        }

        .bank-form-grid.one {
            grid-template-columns: 1fr;
        }

        .bank-form-grid.two {
            grid-template-columns: repeat(2, minmax(0, 1fr));
        }

        .bank-form-grid.three {
            grid-template-columns: repeat(3, minmax(0, 1fr));
        }

        .bank-form-grid.four {
            grid-template-columns: repeat(4, minmax(0, 1fr));
        }

        .field {
            min-width: 0;
        }

        .span-2 {
            grid-column: span 2;
        }

        :host ::ng-deep .p-select,
        :host ::ng-deep .p-inputtext,
        :host ::ng-deep .p-inputnumber,
        :host ::ng-deep .p-inputnumber-input,
        :host ::ng-deep .p-iftalabel {
            width: 100%;
        }

        :host ::ng-deep .p-inputgroupaddon {
            width: 2.9rem;
            color: var(--app-accent-strong);
            background: var(--app-accent-surface);
            border-color: var(--app-border);
        }

        .bank-option,
        .checkbox-row {
            display: flex;
            align-items: center;
            gap: 0.65rem;
        }

        .checkbox-row {
            margin-bottom: 1rem;
        }

        .checkbox-row label {
            color: var(--app-text);
            font-weight: 800;
        }

        .bank-option img {
            width: 32px;
            height: 32px;
            border-radius: 50%;
            object-fit: cover;
            background: #f1f5f9;
        }

        .bank-option div,
        .cell-stack {
            display: flex;
            flex-direction: column;
            gap: 0.15rem;
        }

        .bank-option small,
        .cell-stack small {
            color: var(--app-muted);
        }

        .select-empty {
            color: var(--app-muted);
            padding: 0.75rem;
        }

        .summary-grid {
            display: grid;
            gap: 0.75rem;
        }

        .summary-grid > div {
            min-width: 0;
            padding: 0.85rem;
            border: 1px solid var(--app-border);
            border-radius: 8px;
            background: var(--app-surface-2);
        }

        .summary-grid span {
            display: block;
            color: var(--app-muted);
            font-size: 0.75rem;
            font-weight: 800;
            margin-bottom: 0.35rem;
            text-transform: uppercase;
        }

        .summary-grid strong {
            color: var(--app-text);
            font-weight: 900;
            overflow-wrap: anywhere;
        }

        .summary-grid strong.warn {
            color: var(--app-warn-strong);
        }

        .message-panel {
            display: flex;
            flex-direction: column;
            gap: 0.35rem;
            padding: 1rem;
            border-radius: 8px;
        }

        .message-panel.danger {
            color: var(--app-danger-strong);
            background: var(--app-danger-surface);
            border: 1px solid color-mix(in srgb, var(--app-danger-strong) 28%, transparent);
        }

        .message-panel.warn {
            color: var(--app-warn-strong);
            background: var(--app-warn-surface);
            border: 1px solid color-mix(in srgb, var(--app-warn-strong) 28%, transparent);
        }

        @media (max-width: 1180px) {
            .bank-account-workspace {
                grid-template-columns: 1fr;
            }

            .bank-account-side-column {
                display: grid;
                grid-template-columns: repeat(2, minmax(0, 1fr));
            }
        }

        @media (max-width: 900px) {
            .bank-form-grid.two,
            .bank-form-grid.three,
            .bank-form-grid.four,
            .bank-account-side-column {
                grid-template-columns: 1fr;
            }

            .span-2 {
                grid-column: auto;
            }
        }

        @media (max-width: 560px) {
            .bank-account-page {
                padding: 0.5rem;
            }
        }
    `]
})
export class BankAccountFormComponent implements OnInit {
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly bankAccountService = inject(BankAccountService);
    private readonly chartOfAccountsService = inject(ChartOfAccountsService);
    private readonly partyService = inject(PartyService);
    private readonly sessionStorageService = inject(SessionStorageService);
    private readonly companyService = inject(CompanyService);
    private readonly tenantScopeService = inject(TenantScopeService);

    readonly ownerTypes = Object.values(PartyType).map(value => ({ label: value.replace(/([a-z])([A-Z])/g, '$1 $2'), value }));
    readonly currencies = ['SAR', 'USD', 'EUR', 'AED', 'BHD', 'KWD', 'QAR'].map(value => ({ label: value, value }));

    account: BankAccount = new BankAccount();
    ownerType: PartyType = PartyType.Company;
    partyOptions: Party[] = [];
    selectedOwner?: Party;
    banks: Bank[] = [];
    selectedBank?: Bank;
    chartAccounts: ChartOfAccounts[] = [];
    selectedGlAccount?: ChartOfAccounts;
    activeCompany?: Company;
    activeCountry?: Country;
    loading = false;
    saving = false;
    isEdit = false;

    ngOnInit(): void {
        this.isEdit = Boolean(this.route.snapshot.paramMap.get('id'));
        this.loading = true;
        this.resolveActiveCompany().pipe(
            tap(company => {
                this.activeCompany = company;
                this.activeCountry = this.findCountry(company?.countryId);
                if (company?.id && company.countryId) {
                    this.sessionStorageService.saveCompany(company);
                    this.tenantScopeService.refreshSelectedCompanyDetails(company);
                }
                this.initializeDefaults();
            }),
            switchMap(company => forkJoin({
                banks: company?.countryId ? this.bankAccountService.getBanksByCountryId(company.countryId) : of([] as Bank[]),
                accounts: this.chartOfAccountsService.getAllChartOfAccountsByCompany(company?.id ?? this.companyId, false)
            })),
            catchError(() => of({ banks: [] as Bank[], accounts: [] as ChartOfAccounts[] }))
        ).subscribe(({ banks, accounts }) => {
            this.banks = banks ?? [];
            this.chartAccounts = accounts ?? [];
            this.loading = false;
            if (this.isEdit) {
                this.loadForEdit();
            }
            this.loadParties();
        });
    }

    get canSave(): boolean {
        return this.validationMessages.length === 0;
    }

    get validationMessages(): string[] {
        const messages: string[] = [];
        if (!this.selectedOwner?.id?.entityId) messages.push('Owner is required.');
        if (!this.selectedBank?.id) messages.push('Bank is required.');
        if (!this.account.accountName?.trim()) messages.push('Account name is required.');
        if (!this.account.currencyCode) messages.push('Currency is required.');
        if (!this.account.accountNumber?.trim() && !this.account.iban?.trim()) messages.push('Either account number or IBAN is required.');
        if (this.account.reconciliationEnabled && !this.selectedGlAccount?.accountId) messages.push('GL bank account is required when reconciliation is enabled.');
        return messages;
    }

    get warningMessages(): string[] {
        const messages: string[] = [];
        if (!this.companyCountryId) {
            messages.push('The selected company does not have a country configured; bank master records cannot be filtered.');
        } else if (!this.loading && !this.banks.length) {
            messages.push(`No bank master records were returned for ${this.companyCountryLabel}. Check the selected company country and bank master setup.`);
        }
        if (this.account.reconciliationEnabled) {
            if (!this.account.defaultSuspenseAccountId) messages.push('Suspense account is not configured; suspense receipt/payment adjustments will require manual account selection.');
            if (!this.account.bankFeesAccountId) messages.push('Bank fees account is not configured; fee adjustments will require manual account selection.');
            if (!this.account.interestIncomeAccountId) messages.push('Interest income account is not configured; income adjustments will require manual account selection.');
            if (!this.account.interestExpenseAccountId) messages.push('Interest expense account is not configured; expense adjustments will require manual account selection.');
        }
        return messages;
    }

    get companyCountryLabel(): string {
        const country = this.activeCountry ?? this.findCountry(this.companyCountryId);
        if (country) {
            return `${country.name}${country.countryCode ? ` (${country.countryCode})` : ''}`;
        }
        return this.companyCountryId ? `Unknown country ${this.companyCountryId}` : 'No company country';
    }

    get metrics(): PageMetric[] {
        return [
            { label: 'Company', value: this.activeCompany?.name || 'Current company', hint: `ID ${this.companyId || '-'}` },
            { label: 'Country', value: this.companyCountryLabel, hint: this.companyCountryId ? `ID ${this.companyCountryId}` : 'Missing country', tone: this.companyCountryId ? 'info' : 'danger' },
            { label: 'Bank masters', value: this.banks.length, hint: 'Loaded for company country', tone: this.banks.length ? 'success' : 'warn' },
            { label: 'GL accounts', value: this.chartAccounts.length, hint: 'Available for linkage' }
        ];
    }

    onOwnerTypeChange(): void {
        this.selectedOwner = this.ownerType === PartyType.Company ? this.companyParty() : undefined;
        this.loadParties();
    }

    searchParties(searchText: string): void {
        this.loadParties(searchText);
    }

    loadParties(searchText = ''): void {
        if (this.ownerType === PartyType.Company && !searchText) {
            this.partyOptions = [this.companyParty()];
            this.selectedOwner = this.selectedOwner ?? this.partyOptions[0];
            return;
        }
        this.partyService.getParties(this.companyId, [this.ownerType], searchText, 0, 50).subscribe({
            next: page => {
                const parties = page.content ?? [];
                this.partyOptions = this.ownerType === PartyType.Company ? [this.companyParty(), ...parties.filter(party => party.id.entityId !== this.companyId)] : parties;
                this.selectedOwner = this.selectedOwner ?? this.partyOptions[0];
            },
            error: () => {
                this.partyOptions = this.ownerType === PartyType.Company ? [this.companyParty()] : [];
                this.selectedOwner = this.partyOptions[0];
            }
        });
    }

    save(): void {
        if (!this.canSave || this.saving) return;
        this.saving = true;
        const payload = this.buildPayload();
        const request = this.isEdit && payload.id
            ? this.bankAccountService.updateBankAccount(payload.id, payload)
            : this.bankAccountService.createBankAccount(payload);
        request.subscribe({
            next: saved => this.router.navigate(['/accounts/enterprise/banking/bank-accounts', saved.id]),
            error: () => this.saving = false
        });
    }

    cancel(): void {
        if (this.isEdit && this.account.id) {
            this.router.navigate(['/accounts/enterprise/banking/bank-accounts', this.account.id]);
            return;
        }
        this.router.navigate(['/accounts/enterprise/banking/bank-accounts']);
    }

    private loadForEdit(): void {
        const id = Number(this.route.snapshot.paramMap.get('id'));
        if (!id) return;
        this.bankAccountService.getBankAccountById(id).subscribe(account => {
            this.account = account;
            this.ownerType = account.party?.id?.entityType ?? PartyType.Company;
            this.selectedOwner = account.party ?? this.companyParty();
            this.partyOptions = [this.selectedOwner];
            this.selectedBank = this.banks.find(bank => bank.id === account.bank?.id) ?? account.bank;
            this.selectedGlAccount = this.chartAccounts.find(gl => gl.accountId === account.account?.accountId) ?? account.account;
            this.account.reconciliationVarianceTolerance = this.account.reconciliationVarianceTolerance ?? 0.01;
            this.loadParties();
        });
    }

    private initializeDefaults(): void {
        this.account.owningCompanyId = this.companyId;
        this.account.currencyCode = this.activeCountry?.currencyCode
            ?? this.findCountry(this.companyCountryId)?.currencyCode
            ?? 'SAR';
        this.account.reconciliationEnabled = false;
        this.account.reconciliationVarianceTolerance = 0.01;
        this.selectedOwner = this.companyParty();
        this.partyOptions = [this.selectedOwner];
    }

    private buildPayload(): BankAccount {
        return {
            ...this.account,
            bank: this.selectedBank,
            party: this.selectedOwner as Party,
            account: this.selectedGlAccount as ChartOfAccounts,
            owningCompanyId: this.companyId,
            reconciliationEnabled: Boolean(this.account.reconciliationEnabled),
            reconciliationVarianceTolerance: this.account.reconciliationVarianceTolerance ?? 0.01
        };
    }

    private companyParty(): Party {
        const company = this.activeCompany ?? this.sessionStorageService.getCompany();
        return {
            id: { entityId: company?.id ?? 0, entityType: PartyType.Company },
            entityName: company?.name || 'Current company',
            emailAddress: company?.emailAddress ?? undefined,
            phoneNumber: company?.phoneNumber ?? undefined,
            imageUrl: company?.logo ?? undefined,
            isCompany: true,
            address: company?.address,
            crNumber: company?.crNumber != null ? String(company.crNumber) : undefined,
            taxRegNumber: company?.taxRegNumber ?? undefined,
            owningCompanyId: company?.id ?? 0
        };
    }

    private get companyId(): number {
        const activeScope = this.tenantScopeService.getActiveScope();
        if (activeScope?.type === 'COMPANY') {
            return activeScope.companyId;
        }
        return this.activeCompany?.id
            ?? this.sessionStorageService.getCompany()?.id
            ?? this.sessionStorageService.getCustomer()?.company?.id
            ?? 0;
    }

    private get companyCountryId(): number {
        return this.activeCompany?.countryId
            ?? this.sessionStorageService.getCompany()?.countryId
            ?? this.sessionStorageService.getCustomer()?.company?.countryId
            ?? 0;
    }

    private resolveActiveCompany() {
        const companyId = this.companyId;
        const cachedCompany = [this.sessionStorageService.getCompany(), this.sessionStorageService.getCustomer()?.company]
            .find(company => company?.id === companyId && !!company.countryId);
        if (cachedCompany) {
            return of(cachedCompany);
        }
        if (!companyId) {
            return of(this.sessionStorageService.getCompany() ?? this.sessionStorageService.getCustomer()?.company ?? {} as Company);
        }
        const activeScope = this.tenantScopeService.getActiveScope();
        const activeCompanyName = activeScope?.type === 'COMPANY' ? activeScope.companyName : undefined;
        return this.companyService.getCompanyById(companyId).pipe(
            map(response => response?.data ?? ({ id: companyId, name: activeCompanyName ?? 'Company' } as Company)),
            catchError(() => of({
                ...(this.sessionStorageService.getCompany() ?? {}),
                id: companyId,
                name: activeCompanyName ?? this.sessionStorageService.getCompany()?.name ?? 'Company'
            } as Company))
        );
    }

    private findCountry(countryId?: number): Country | undefined {
        return this.sessionStorageService.getCountryList()?.find(country => country.id === countryId);
    }
}
